import { hexToRgb } from "@nens/shared-utils";

export type RasterColorStop = {
  value: number;
  color: string;
};

export type RasterRgbStop = {
  value: number;
  rgb: [number, number, number];
};

/**
 * Remap stop values from the style domain (sourceRange) to a raster domain (targetRange).
 *
 * Example: style min/max 0..100 can be remapped to measured raster min/max 12..42,
 * so both map rendering and legend labels follow the same value range.
 */
export function remapStopsToRange({
  stops,
  sourceRange,
  targetRange,
}: {
  stops: RasterColorStop[];
  sourceRange: [number, number] | undefined;
  targetRange: [number, number] | undefined;
}) {
  if (!sourceRange || !targetRange) return stops;

  const [sourceMin, sourceMax] = sourceRange;
  const [targetMin, targetMax] = targetRange;
  const sourceSpan = sourceMax - sourceMin;
  const targetSpan = targetMax - targetMin;

  if (sourceSpan <= 0 || targetSpan <= 0) return stops;

  return stops.map((stop) => ({
    ...stop,
    value: targetMin + ((stop.value - sourceMin) / sourceSpan) * targetSpan,
  }));
}

export function toRgbStops(stops: RasterColorStop[]) {
  const rgbStops: RasterRgbStop[] = [];

  for (const stop of stops) {
    try {
      rgbStops.push({ value: stop.value, rgb: hexToRgb(stop.color) });
    } catch {
      // Invalid colors are skipped so a single bad stop does not break rendering.
    }
  }

  // Ensure predictable interpolation order even when input stops are unsorted.
  return rgbStops.sort((a, b) => a.value - b.value);
}

/**
 * Interpolate an RGB color for a numeric value on an ordered stop list.
 * Values outside the stop range are clamped to the nearest end color.
 */
export function interpolateRgbByStops(value: number, stops: RasterRgbStop[]) {
  if (value <= stops[0].value) return stops[0].rgb;
  if (value >= stops[stops.length - 1].value) return stops[stops.length - 1].rgb;

  for (let i = 1; i < stops.length; i++) {
    const prev = stops[i - 1];
    const next = stops[i];

    if (value <= next.value) {
      const span = next.value - prev.value;
      if (span <= 0) return next.rgb;

      const ratio = (value - prev.value) / span;
      return [
        Math.round(prev.rgb[0] + (next.rgb[0] - prev.rgb[0]) * ratio),
        Math.round(prev.rgb[1] + (next.rgb[1] - prev.rgb[1]) * ratio),
        Math.round(prev.rgb[2] + (next.rgb[2] - prev.rgb[2]) * ratio),
      ] as [number, number, number];
    }
  }

  return stops[stops.length - 1].rgb;
}
